import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  toBase, fromBase, altsFor, fmt, UNITS,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  POOL TURNOVER RATE
 *  --------------------------------------------------------------
 *  Turnover (h) = Volume / Flow.  SANS 10134 calls for a 6 h
 *  turnover on public / commercial pools; 8 h is typical for
 *  residential.
 *  --------------------------------------------------------------
 */
const TARGETS = [
  { value: "6",  label: "6 hours — SANS 10134 public / commercial" },
  { value: "8",  label: "8 hours — residential pools" },
  { value: "4",  label: "4 hours — heavy-bather load" },
  { value: "12", label: "12 hours — low-use / cool-water pond" },
];

export default function PoolTurnoverCalculator() {
  const [volVal, setVolVal] = useState(50);
  const [volUnit, setVolUnit] = useState("m3");
  const [flowVal, setFlowVal] = useState(8);
  const [flowUnit, setFlowUnit] = useState("m3h");
  const [target, setTarget] = useState("6");

  const vol_L  = toBase("volume", volVal, volUnit);
  const Q_m3s  = toBase("flow", flowVal, flowUnit);
  const Q_Lh   = Q_m3s * 3600 * 1000;

  const turnover_h = Q_Lh > 0 ? vol_L / Q_Lh : null;
  const turnoversPerDay = turnover_h ? 24 / turnover_h : null;

  const targetH = parseFloat(target);
  // Required flow to meet target turnover, in L/h
  const requiredFlow_Lh = targetH > 0 ? vol_L / targetH : null;
  const requiredFlow_m3s = requiredFlow_Lh ? requiredFlow_Lh / (3600 * 1000) : null;

  const status = !turnover_h ? undefined
              : turnover_h > targetH * 1.2 ? "alert"
              : turnover_h > targetH        ? "warn"
              : "ok";
  const note = !turnover_h ? null
            : turnover_h > targetH * 1.2
            ? `Above the ${targetH} h target by ${fmt(((turnover_h / targetH) - 1) * 100, 0)} % — pump is undersized.`
            : turnover_h > targetH
            ? `Slightly above the ${targetH} h target — within acceptable margin.`
            : `Within the ${targetH} h target. ${fmt(turnoversPerDay)} turnovers / day.`;

  return (
    <ToolShell
      slug="pool-turnover"
      title="Pool turnover rate"
      oneLiner="Hours to circulate the full pool volume — and the pump flow required to hit a SANS or residential target."
      onExport={() => ({
        inputs: [
          { label: "Pool volume", value: volVal, unit: volUnit },
          { label: "Pump flow rate", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Target turnover", value: TARGETS.find((t) => t.value === target)?.label ?? `${targetH} h` },
        ],
        results: [
          { label: "Turnover time", value: fmt(turnover_h), unit: "hours",
            status, note },
          { label: "Turnovers per day", value: fmt(turnoversPerDay), unit: "/ day" },
          { label: "Required flow to hit target",
            value: fmt(requiredFlow_m3s ? requiredFlow_m3s * 3600 : null), unit: "m³/h",
            alts: requiredFlow_m3s !== null ? altsFor("flow", requiredFlow_m3s, ["Lps", "Lpm", "gpm"], 1) : null,
            note: `Sized for the ${targetH} h target.` },
        ],
        notes: ["Formula: Turnover (h) = Volume / Flow rate."],
        spec: turnover_h !== null ? {
          title: "Pool circulation specification",
          body: `A <strong>${volVal} ${volUnit}</strong> pool circulated at <strong>${flowVal} ${UNITS.flow.labels[flowUnit]}</strong> turns over in <strong>${fmt(turnover_h, 1)} hours</strong> (${fmt(turnoversPerDay, 1)} turnovers/day). To hit the <strong>${targetH} h</strong> target, specify a pump capable of <strong>${fmt(requiredFlow_m3s ? requiredFlow_m3s * 3600 : null, 2)} m³/h</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Pool volume">
          <UnitNumber dim="volume" value={volVal} onChange={setVolVal} unit={volUnit} onUnitChange={setVolUnit}
            units={["L", "kL", "m3", "USgal"]} testid="pt-vol" />
        </Field>
        <Field label="Pump flow rate">
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "gpm"]} testid="pt-flow" />
        </Field>
        <Field label="Target turnover" span={2}>
          <Select value={target} onChange={setTarget} options={TARGETS} testid="pt-target" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result label="Turnover time"
          primary={{ value: fmt(turnover_h), unit: "hours" }}
          status={status} note={note}
          testid="pt-result-time" />
        <Result label="Turnovers per day"
          primary={{ value: fmt(turnoversPerDay), unit: "/ day" }}
          testid="pt-result-perday" />
        <Result label={`Required flow to hit ${targetH} h`}
          primary={{ value: fmt(requiredFlow_m3s ? requiredFlow_m3s * 3600 : null), unit: "m³/h" }}
          alts={requiredFlow_m3s !== null ? altsFor("flow", requiredFlow_m3s, ["Lps", "Lpm", "gpm"], 1) : null}
          testid="pt-result-required" />
      </div>

      {turnover_h !== null && (
        <SpecCard
          eyebrow="Pool circulation specification"
          testid="pt-spec-card"
          footer={note}
        >
          A <SpecValue>{volVal} {volUnit}</SpecValue> pool circulated at{" "}
          <SpecValue>{flowVal} {UNITS.flow.labels[flowUnit]}</SpecValue> turns over in{" "}
          <SpecValue>{fmt(turnover_h, 1)} hours</SpecValue> ({fmt(turnoversPerDay, 1)} turnovers/day). To hit the{" "}
          <SpecValue>{targetH} h</SpecValue> target, specify a pump capable of{" "}
          <SpecValue>{fmt(requiredFlow_m3s ? requiredFlow_m3s * 3600 : null, 2)} m³/h</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}
